import { zodResolver } from '@hookform/resolvers/zod';
import {
  ErrorCode,
  requestSignatureProofSchema,
  signedCommandSchema,
  SIGNABLE_ACTS,
  SIGNATURE_MEANING_TEXT_PT_BR,
  type SignableAct,
  type SignedCommand,
} from '@qualicloud/contracts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import {
  Button,
  Dialog,
  Field,
  FormError,
  IconAlertOctagon,
  IconClock,
  IconKey,
  IconShieldCheck,
  IconSignature,
  IconUser,
  Input,
  PasswordInput,
  Textarea,
  cn,
} from '@/design-system';
import { ApiError, signatureApi } from '@/lib/api';
import { formatCountdown } from '@/lib/format/datetime';
import { useAuth } from '@/providers/auth-provider';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIÁLOGO DE ASSINATURA ELETRÔNICA — 21 CFR Part 11, Subparte C
 * Fiel ao fluxo do plano §5.1.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * §11.50(a) — a assinatura precisa exibir, no momento do ato:
 *     · o nome impresso do signatário            → bloco "Signatário"
 *     · a data e a hora do ato                   → carimbo do servidor, exibido
 *                                                   depois no manifesto
 *     · o SIGNIFICADO da assinatura              → bloco "Significado", texto
 *                                                   FIXO e NÃO EDITÁVEL vindo de
 *                                                   SIGNATURE_MEANING_TEXT_PT_BR
 *
 * §11.200(a)(1)(i) — assinatura não biométrica exige DOIS componentes
 *     distintos de identificação. Aqui são campos separados de usuário e senha,
 *     e o usuário é pedido MESMO havendo sessão aberta: reconfirmar quem se é
 *     faz parte do ato e impede assinatura a partir de máquina desbloqueada de
 *     terceiro. O IDENTITY recusa com SIGNATURE.IDENTITY_MISMATCH se o usuário
 *     digitado não for o titular da sessão.
 *
 * §11.200(a)(2) — a assinatura só pode ser usada pelo seu titular genuíno.
 *     Não há campo "assinar em nome de". Não existe delegação.
 *
 * §11.10(g) — verificação de autoridade: o IDENTITY confere a permissão do ato
 *     antes de emitir a prova (SIGNATURE.NOT_AUTHORIZED_FOR_ACT).
 *
 * FLUXO (plano §5.1)
 *   1. usuário aciona a ação
 *   2. este diálogo abre exibindo o que será assinado, o significado e o nome
 *   3. usuário digita usuário + senha (+ TOTP quando exigido) → IDENTITY
 *      ← signature_proof (uso único, TTL 60 s)
 *   4. o diálogo envia a AÇÃO + a prova ao CORE
 *   5. o CORE consome a prova, grava a assinatura e aplica o efeito de negócio
 *      NA MESMA TRANSAÇÃO
 *
 * A contagem regressiva de 60 s é exibida a partir do passo 3 porque a prova
 * pode expirar entre a emissão e o commit — e o usuário precisa entender por
 * que, nesse caso, terá de confirmar a identidade de novo.
 *
 * ACESSIBILIDADE
 *   · `dismissible={false}` durante os passos 3–5: fechar no meio do ato
 *     deixaria uma prova queimada sem efeito registrado, e o usuário sem saber
 *     se assinou ou não;
 *   · a contagem regressiva é `aria-live="off"` com anúncio pontual em 30 s,
 *     10 s e 0 s — anunciar segundo a segundo tornaria o leitor de tela inútil;
 *   · toda mensagem de erro é `role="alert"` e diz explicitamente se o registro
 *     foi ou não alterado;
 *   · o foco inicial vai para o campo "Usuário", nunca para o botão Assinar.
 */

export interface SignatureDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Ato do catálogo `SIGNABLE_ACTS`. Define significado e obrigatoriedade de justificativa. */
  readonly act: SignableAct;
  /** UUID do registro assinado. Precisa bater com o que o CORE espera. */
  readonly recordId: string;
  /** Identificação legível: "POP-042 — Procedimento de Limpeza". */
  readonly recordLabel: string;
  /** Versão do registro, quando houver: "v2.0". */
  readonly recordVersion?: string;
  /** Linhas extras de contexto exibidas no bloco "O que será assinado". */
  readonly recordDetails?: ReadonlyArray<{ readonly label: string; readonly value: string }>;
  /** A organização exige segundo fator para assinar. */
  readonly mfaRequired?: boolean;
  /**
   * Executa o ato no CORE com a prova recém-emitida.
   * Deve lançar `ApiError` em falha — o diálogo traduz o `ErrorCode`.
   */
  readonly onSign: (command: SignedCommand) => Promise<void>;
  readonly onSigned?: () => void;
  /** Rótulo do botão de confirmação. Padrão: rótulo pt-BR do ato. */
  readonly confirmLabel?: string;
}

type DialogPhase = 'form' | 'requesting-proof' | 'signing' | 'done';

/** TTL da prova, definido pelo IDENTITY. Usado só para a barra de progresso. */
const PROOF_TTL_MS = 60_000;

/**
 * Valores do formulário.
 *
 * Declarados como interface estável (e não inferidos de um schema criado a cada
 * render) para que o tipo do `useForm` não dependa do estado de MFA, que muda
 * em tempo de execução.
 */
interface SignatureFormValues {
  username: string;
  password: string;
  totp?: string;
  reason?: string;
}

export function SignatureDialog({
  open,
  onOpenChange,
  act,
  recordId,
  recordLabel,
  recordVersion,
  recordDetails,
  mfaRequired = false,
  onSign,
  onSigned,
  confirmLabel,
}: SignatureDialogProps) {
  const { user } = useAuth();
  const definition = SIGNABLE_ACTS[act];
  const meaningText = SIGNATURE_MEANING_TEXT_PT_BR[definition.meaning];

  const [phase, setPhase] = useState<DialogPhase>('form');
  const [failure, setFailure] = useState<SignatureFailure | null>(null);
  const [needsTotp, setNeedsTotp] = useState(mfaRequired);
  const [proofExpiresAt, setProofExpiresAt] = useState<Date | null>(null);

  /*
   * O schema muda quando a organização revela a exigência de MFA no meio do
   * fluxo (AUTH.MFA_REQUIRED). O resolver tem identidade ESTÁVEL e lê o schema
   * corrente por referência — assim a validação nunca roda contra um schema
   * desatualizado, e o `useForm` não precisa ser recriado.
   */
  const reasonRequired = definition.reasonRequired;
  const schemaRef = useRef(buildFormSchema(reasonRequired, mfaRequired));

  useEffect(() => {
    schemaRef.current = buildFormSchema(reasonRequired, needsTotp);
  }, [reasonRequired, needsTotp]);

  const resolver = useCallback<Resolver<SignatureFormValues>>(
    (values, context, options) =>
      (zodResolver(schemaRef.current) as Resolver<SignatureFormValues>)(values, context, options),
    [],
  );

  const form = useForm<SignatureFormValues>({
    resolver,
    mode: 'onSubmit',
    defaultValues: { username: '', password: '', totp: '', reason: '' },
  });

  // Reset completo a cada abertura: a senha JAMAIS permanece em memória de
  // formulário entre dois atos assinados.
  useEffect(() => {
    if (!open) return;
    form.reset({ username: '', password: '', totp: '', reason: '' });
    setPhase('form');
    setFailure(null);
    setNeedsTotp(mfaRequired);
    setProofExpiresAt(null);
  }, [open, mfaRequired, form]);

  const busy = phase === 'requesting-proof' || phase === 'signing';

  const handleSubmit = form.handleSubmit(async (values) => {
    setFailure(null);

    // ── Passo 3 do plano §5.1: prova de assinatura junto ao IDENTITY ────────
    setPhase('requesting-proof');
    let proof: { proofId: string; token: string; expiresAt: Date };
    try {
      proof = await signatureApi.requestProof({
        act,
        recordType: definition.recordType,
        recordId,
        username: values.username.trim(),
        password: values.password,
        ...(values.totp ? { totp: values.totp } : {}),
      });
    } catch (error) {
      const parsed = classifyFailure(error, 'proof');
      if (parsed.code === ErrorCode.MFA_REQUIRED) {
        // A organização passou a exigir MFA: revela o campo e mantém o resto.
        setNeedsTotp(true);
      }
      setFailure(parsed);
      setPhase('form');
      form.setValue('password', '');
      return;
    }

    setProofExpiresAt(proof.expiresAt);

    // ── Passos 4 e 5: envia o ato + a prova ao CORE ─────────────────────────
    setPhase('signing');
    try {
      await onSign({
        proofId: proof.proofId,
        proofToken: proof.token,
        ...(values.reason?.trim() ? { reason: values.reason.trim() } : {}),
      });
    } catch (error) {
      setFailure(classifyFailure(error, 'act'));
      setPhase('form');
      // A prova já foi consumida (ou queimada). Refazer exige nova senha.
      form.setValue('password', '');
      form.setValue('totp', '');
      setProofExpiresAt(null);
      return;
    }

    setPhase('done');
    onSigned?.();
    onOpenChange(false);
  });

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next && busy) return; // não permite abandonar o ato no meio
      onOpenChange(next);
    },
    [busy, onOpenChange],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      dismissible={!busy}
      size="md"
      title={
        <span className="flex items-center gap-2">
          <IconSignature size={20} className="text-brand" aria-hidden="true" />
          Assinatura eletrônica
        </span>
      }
      description="Confirme sua identidade para concluir este ato. A assinatura é registrada de forma permanente."
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant={definition.meaning === 'REJECTED' ? 'danger' : 'primary'}
            loading={busy}
            loadingLabel={
              phase === 'requesting-proof'
                ? 'Validando sua identidade…'
                : 'Registrando a assinatura…'
            }
            leadingIcon={busy ? undefined : <IconShieldCheck size={16} />}
            onClick={() => void handleSubmit()}
          >
            {confirmLabel ?? definition.labelPtBr}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
        className="flex flex-col gap-5"
        noValidate
      >
        {/* ── §11.50(a): O QUE SERÁ ASSINADO ──────────────────────────────── */}
        <section
          aria-labelledby="signature-record-heading"
          className="rounded-md border border-border bg-surface-sunken p-3.5"
        >
          <h3
            id="signature-record-heading"
            className="text-2xs font-semibold uppercase tracking-wide text-content-subtle"
          >
            O que será assinado
          </h3>
          <p className="mt-1.5 text-base font-semibold text-content">{recordLabel}</p>
          {recordVersion ? (
            <p className="text-sm text-content-muted">
              Versão <span className="font-medium text-content qc-tabular">{recordVersion}</span>
            </p>
          ) : null}
          {recordDetails && recordDetails.length > 0 ? (
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              {recordDetails.map((detail) => (
                <div key={detail.label} className="contents">
                  <dt className="text-content-subtle">{detail.label}</dt>
                  <dd className="text-content">{detail.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </section>

        {/* ── §11.50(a): SIGNIFICADO — texto fixo, NÃO editável ────────────── */}
        <section
          aria-labelledby="signature-meaning-heading"
          className="rounded-md border-l-4 border-l-brand border border-border bg-brand-subtle p-3.5"
        >
          <h3
            id="signature-meaning-heading"
            className="text-2xs font-semibold uppercase tracking-wide text-brand"
          >
            Significado desta assinatura
          </h3>
          {/*
            Este texto é DADO, não conteúdo de formulário: vem de
            SIGNATURE_MEANING_TEXT_PT_BR e é congelado no registro no momento do
            ato (`meaning_text`). Não há input, não há select, não há como
            alterar — §11.50(a) exige que o significado seja inequívoco, e
            §11.50(b) exige que a exibição não mude retroativamente.
          */}
          <p className="mt-1 text-lg font-semibold text-content">{meaningText}</p>
          <p className="mt-1 text-sm text-content-muted">
            Ao assinar, você declara: <strong className="font-semibold">{meaningText.toLowerCase()}</strong>{' '}
            este registro, na qualidade de{' '}
            <span className="font-medium text-content">{user?.jobTitle ?? 'usuário do sistema'}</span>.
          </p>
        </section>

        {/* ── §11.50(a): NOME DO SIGNATÁRIO ───────────────────────────────── */}
        <section
          aria-labelledby="signature-signer-heading"
          className="flex items-start gap-3 rounded-md border border-border bg-surface p-3.5"
        >
          <span
            aria-hidden="true"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-content-subtle"
          >
            <IconUser size={18} />
          </span>
          <div className="min-w-0">
            <h3
              id="signature-signer-heading"
              className="text-2xs font-semibold uppercase tracking-wide text-content-subtle"
            >
              Signatário
            </h3>
            <p className="text-base font-medium text-content">
              {user?.displayName ?? 'Sessão não identificada'}
            </p>
            <p className="text-sm text-content-muted">
              {user?.jobTitle}
              {user?.username ? ` · ${user.username}` : ''}
            </p>
          </div>
        </section>

        {/* ── Erro ────────────────────────────────────────────────────────── */}
        {failure ? <SignatureFailureBanner failure={failure} /> : null}

        {/* ── Contagem regressiva da prova ────────────────────────────────── */}
        {phase === 'signing' && proofExpiresAt ? (
          <ProofCountdown expiresAt={proofExpiresAt} />
        ) : null}

        {/* ── §11.200(a)(1)(i): DOIS componentes de identificação ─────────── */}
        <fieldset className="m-0 flex flex-col gap-3 border-0 p-0" disabled={busy}>
          <legend className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-content">
            <IconKey size={15} aria-hidden="true" className="text-content-subtle" />
            Confirmação de identidade
          </legend>
          <p className="-mt-1 mb-1 text-xs text-content-subtle">
            Informe seu usuário e sua senha. Os dois componentes são exigidos pelo 21 CFR
            §11.200(a)(1)(i) mesmo com a sessão já aberta — não existe delegação de assinatura.
          </p>

          <Field
            label="Usuário"
            required
            error={form.formState.errors.username?.message}
            description="Precisa ser o mesmo usuário da sessão atual."
          >
            <Input
              {...form.register('username')}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              // Foco inicial no primeiro campo, nunca no botão de confirmar.
              autoFocus
            />
          </Field>

          <Field label="Senha" required error={form.formState.errors.password?.message}>
            <PasswordInput {...form.register('password')} autoComplete="current-password" />
          </Field>

          {needsTotp ? (
            <Field
              label="Código de verificação"
              required
              error={form.formState.errors.totp?.message}
              description="Código de 6 dígitos do seu aplicativo autenticador."
            >
              <Input
                {...form.register('totp')}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className="w-32 text-center font-mono text-lg tracking-[0.4em]"
              />
            </Field>
          ) : null}

          {/* ── §11.50: justificativa quando o ato exige ──────────────────── */}
          {definition.reasonRequired ? (
            <Field
              label="Justificativa"
              required
              error={form.formState.errors.reason?.message}
              description="Este ato exige justificativa. O texto é gravado junto da assinatura e não pode ser alterado depois."
            >
              <Textarea
                {...form.register('reason')}
                rows={3}
                maxLength={2000}
                showCounter
                value={form.watch('reason') ?? ''}
                placeholder="Descreva o motivo desta decisão."
              />
            </Field>
          ) : null}
        </fieldset>

        {/* ── Aviso legal ─────────────────────────────────────────────────── */}
        <p
          className={cn(
            'flex items-start gap-2 rounded-md border border-warning bg-warning-subtle px-3 py-2.5',
            'text-sm text-warning-content',
          )}
        >
          <IconAlertOctagon size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
          <span>
            <strong className="font-semibold">Esta assinatura é legalmente vinculante.</strong>{' '}
            Conforme o 21 CFR Part 11, ela é o equivalente legal da sua assinatura manuscrita. O ato
            será registrado de forma permanente na trilha de auditoria e não poderá ser desfeito.
          </span>
        </p>

        {/* Submit oculto: permite Enter em qualquer campo sem duplicar o botão. */}
        <button type="submit" className="qc-sr-only" tabIndex={-1} aria-hidden="true">
          Assinar
        </button>
      </form>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Schema do formulário
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Composto a partir dos schemas do contrato — nada é redefinido.
 *
 * `requestSignatureProofSchema` já valida usuário, senha e o formato do TOTP;
 * `signedCommandSchema` já valida o limite da justificativa. Aqui só
 * selecionamos os campos que o formulário coleta e acrescentamos a
 * obrigatoriedade condicional da justificativa, que no servidor é decidida por
 * `SIGNABLE_ACTS[act].reasonRequired`.
 */
export function buildFormSchema(reasonRequired: boolean, totpRequired: boolean) {
  const base = requestSignatureProofSchema
    .pick({ username: true, password: true, totp: true })
    .extend({
      totp: requestSignatureProofSchema.shape.totp.or(z.literal('')).optional(),
      reason: signedCommandSchema.shape.reason.or(z.literal('')).optional(),
    });

  return base.superRefine((values, ctx) => {
    if (reasonRequired && !values.reason?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reason'],
        message: 'Este ato exige justificativa.',
      });
    }
    if (totpRequired && !/^\d{6}$/.test(values.totp ?? '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['totp'],
        message: 'Informe o código de 6 dígitos.',
      });
    }
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Tratamento de erro
// ─────────────────────────────────────────────────────────────────────────────

export interface SignatureFailure {
  readonly code: string;
  readonly title: string;
  readonly message: string;
  /**
   * Se o registro foi ou não alterado. Em ambiente regulado esta é a primeira
   * pergunta do usuário depois de um erro, e a resposta precisa estar na tela.
   */
  readonly recordChanged: 'no' | 'unknown';
  readonly canRetry: boolean;
}

/**
 * `ErrorCode` → mensagem específica do contexto de assinatura.
 *
 * Cada caminho de erro do fluxo tem redação própria porque a consequência é
 * diferente em cada um: senha errada dá para refazer na hora; prova consumida
 * exige recomeçar; autoaprovação bloqueada nunca vai funcionar para este
 * usuário. Um texto genérico faria o usuário tentar de novo no caso errado.
 */
function classifyFailure(error: unknown, stage: 'proof' | 'act'): SignatureFailure {
  if (!(error instanceof ApiError)) {
    return {
      code: 'UNKNOWN',
      title: 'Não foi possível concluir a assinatura',
      message:
        'Ocorreu um erro inesperado. Verifique se o ato foi registrado antes de tentar novamente.',
      recordChanged: 'unknown',
      canRetry: true,
    };
  }

  switch (error.code) {
    // ── Falhas na emissão da prova (§11.200) ────────────────────────────────
    case ErrorCode.INVALID_CREDENTIALS:
      return {
        code: error.code,
        title: 'Senha incorreta',
        message:
          'A senha informada não confere. Nenhuma alteração foi registrada. Atenção: tentativas malsucedidas contam para o bloqueio da conta, do mesmo modo que na tela de entrada.',
        recordChanged: 'no',
        canRetry: true,
      };

    case ErrorCode.SIGNATURE_IDENTITY_MISMATCH:
      return {
        code: error.code,
        title: 'Usuário diferente do titular da sessão',
        message:
          'A assinatura só pode ser executada pelo titular da sessão. Informe o seu próprio usuário — o 21 CFR §11.200(a)(2) não admite delegação nem procuração de assinatura. Nenhuma alteração foi registrada.',
        recordChanged: 'no',
        canRetry: true,
      };

    case ErrorCode.MFA_REQUIRED:
      return {
        code: error.code,
        title: 'Segundo fator obrigatório',
        message:
          'Esta organização exige o segundo fator para assinar. Informe o código de 6 dígitos do seu aplicativo autenticador.',
        recordChanged: 'no',
        canRetry: true,
      };

    case ErrorCode.MFA_INVALID:
      return {
        code: error.code,
        title: 'Código de verificação inválido',
        message:
          'O código informado é inválido ou já expirou. Aguarde o próximo código no aplicativo autenticador e tente de novo. Nenhuma alteração foi registrada.',
        recordChanged: 'no',
        canRetry: true,
      };

    case ErrorCode.ACCOUNT_LOCKED:
      return {
        code: error.code,
        title: 'Conta bloqueada',
        message:
          'A conta foi bloqueada por excesso de tentativas malsucedidas. Aguarde o fim do bloqueio ou solicite o desbloqueio ao administrador da organização. Nenhuma alteração foi registrada.',
        recordChanged: 'no',
        canRetry: false,
      };

    case ErrorCode.SIGNATURE_NOT_AUTHORIZED_FOR_ACT:
      return {
        code: error.code,
        title: 'Sem autoridade para este ato',
        message:
          'Sua conta não tem autoridade para executar este ato assinado (21 CFR §11.10(g)). Procure o gestor da qualidade da sua organização. Nenhuma alteração foi registrada.',
        recordChanged: 'no',
        canRetry: false,
      };

    case ErrorCode.RATE_LIMITED:
      return {
        code: error.code,
        title: 'Muitas tentativas',
        message:
          'Foram feitas muitas tentativas em pouco tempo. Aguarde alguns instantes antes de tentar novamente. Nenhuma alteração foi registrada.',
        recordChanged: 'no',
        canRetry: true,
      };

    // ── Falhas no consumo da prova (§11.70) ─────────────────────────────────
    case ErrorCode.SIGNATURE_PROOF_EXPIRED:
      return {
        code: error.code,
        title: 'O prazo de 60 segundos expirou',
        message:
          'A confirmação de identidade vale por 60 segundos e esse prazo terminou antes de o ato ser concluído. Nenhuma alteração foi registrada. Informe usuário e senha novamente.',
        recordChanged: 'no',
        canRetry: true,
      };

    case ErrorCode.SIGNATURE_PROOF_ALREADY_USED:
      return {
        code: error.code,
        title: 'Confirmação já utilizada',
        message:
          'Cada confirmação de identidade vale para um único ato e esta já foi consumida. Se o ato anterior foi concluído, feche esta janela e recarregue a página para ver a situação atual. Caso contrário, confirme sua identidade novamente.',
        recordChanged: 'unknown',
        canRetry: true,
      };

    case ErrorCode.SIGNATURE_PROOF_INVALID:
      return {
        code: error.code,
        title: 'Confirmação de identidade inválida',
        message:
          'A confirmação de identidade não pôde ser validada pelo servidor. Nenhuma alteração foi registrada. Refaça a operação.',
        recordChanged: 'no',
        canRetry: true,
      };

    case ErrorCode.SIGNATURE_PROOF_RECORD_MISMATCH:
      return {
        code: error.code,
        title: 'Confirmação não corresponde a este registro',
        message:
          'A confirmação de identidade foi emitida para outro registro. Por segurança, ela não pode ser reaproveitada. Feche esta janela e recomece a partir do documento correto.',
        recordChanged: 'no',
        canRetry: false,
      };

    case ErrorCode.SIGNATURE_REASON_REQUIRED:
      return {
        code: error.code,
        title: 'Justificativa obrigatória',
        message:
          'Este ato exige justificativa e ela não foi recebida. Preencha o campo Justificativa. Nenhuma alteração foi registrada.',
        recordChanged: 'no',
        canRetry: true,
      };

    case ErrorCode.SIGNATURE_RECORD_MUTATED:
      return {
        code: error.code,
        title: 'O registro mudou desde que você abriu a tela',
        message:
          'O conteúdo do registro foi alterado por outra pessoa depois que esta tela foi aberta. A assinatura não foi aplicada, para que você não assine algo diferente do que leu. Recarregue a página e revise o conteúdo.',
        recordChanged: 'no',
        canRetry: false,
      };

    // ── Falhas de regra de negócio no ato ───────────────────────────────────
    case ErrorCode.DOCUMENT_SELF_APPROVAL:
      return {
        code: error.code,
        title: 'O autor não pode aprovar a própria versão',
        message:
          'A segregação de funções exigida pela ISO 13485 §4.2.4 está ativa nesta organização: quem elabora a versão não pode aprová-la. Nenhuma alteração foi registrada.',
        recordChanged: 'no',
        canRetry: false,
      };

    case ErrorCode.DOCUMENT_NOT_APPROVER:
      return {
        code: error.code,
        title: 'Você não é aprovador desta versão',
        message:
          'Sua conta não está entre os aprovadores designados na distribuição deste documento. Nenhuma alteração foi registrada.',
        recordChanged: 'no',
        canRetry: false,
      };

    case ErrorCode.DOCUMENT_ALREADY_DECIDED:
      return {
        code: error.code,
        title: 'Você já decidiu esta versão',
        message:
          'Cada aprovador registra uma única decisão por versão, e a sua já consta. Recarregue a página para ver o andamento das aprovações.',
        recordChanged: 'no',
        canRetry: false,
      };

    case ErrorCode.DOCUMENT_ILLEGAL_TRANSITION:
      return {
        code: error.code,
        title: 'Operação incompatível com a situação atual',
        message:
          'A situação do documento mudou e este ato não é mais válido para ela. Recarregue a página para ver a situação vigente. Nenhuma alteração foi registrada.',
        recordChanged: 'no',
        canRetry: false,
      };

    case ErrorCode.FORBIDDEN:
      return {
        code: error.code,
        title: 'Ação não permitida',
        message:
          'Você não tem permissão para executar esta ação sobre este documento. Nenhuma alteração foi registrada.',
        recordChanged: 'no',
        canRetry: false,
      };

    case 'NETWORK.UNAVAILABLE':
      return {
        code: error.code,
        title: 'Falha de comunicação',
        message:
          stage === 'proof'
            ? 'Não foi possível falar com o servidor. Nenhuma alteração foi registrada. Verifique sua conexão e tente novamente.'
            : 'A conexão caiu durante o registro da assinatura. NÃO é possível garantir se o ato foi concluído. Recarregue a página e confira a situação do registro antes de tentar de novo.',
        recordChanged: stage === 'proof' ? 'no' : 'unknown',
        canRetry: true,
      };

    default:
      return {
        code: error.code,
        title: 'A assinatura não foi concluída',
        message: `${error.message}${
          error.correlationId ? ` (código de correlação: ${error.correlationId})` : ''
        }`,
        recordChanged: stage === 'proof' ? 'no' : 'unknown',
        canRetry: true,
      };
  }
}

function SignatureFailureBanner({ failure }: { readonly failure: SignatureFailure }) {
  return (
    <FormError title={failure.title}>
      <p className="mt-0.5">{failure.message}</p>
      {failure.recordChanged === 'unknown' ? (
        <p className="mt-2 font-semibold">
          Confira a situação do registro antes de repetir a operação.
        </p>
      ) : null}
      <p className="mt-2 text-xs opacity-80">
        Código do erro: <span className="font-mono">{failure.code}</span>
      </p>
    </FormError>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Contagem regressiva
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Contagem regressiva da prova (TTL de 60 s).
 *
 * ACESSIBILIDADE: o número visível atualiza a cada segundo, mas o `aria-live`
 * fica desligado e o anúncio é PONTUAL — 30 s, 10 s e no fim. Um `aria-live`
 * a cada segundo inutilizaria o leitor de tela justamente no momento em que o
 * usuário mais precisa ouvir o resultado do ato.
 */
export function ProofCountdown({ expiresAt }: { readonly expiresAt: Date }) {
  const [remaining, setRemaining] = useState(() => expiresAt.getTime() - Date.now());
  const [announcement, setAnnouncement] = useState('');
  const announcedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    announcedRef.current = new Set();
    const tick = () => {
      const next = expiresAt.getTime() - Date.now();
      setRemaining(next);

      const seconds = Math.ceil(next / 1000);
      for (const milestone of [30, 10, 0]) {
        if (seconds <= milestone && !announcedRef.current.has(milestone)) {
          announcedRef.current.add(milestone);
          setAnnouncement(
            milestone === 0
              ? 'O prazo da confirmação de identidade expirou.'
              : `Restam ${milestone} segundos para concluir a assinatura.`,
          );
        }
      }
    };

    tick();
    const interval = window.setInterval(tick, 500);
    return () => window.clearInterval(interval);
  }, [expiresAt]);

  const expired = remaining <= 0;
  const percent = Math.max(0, Math.min(100, (remaining / PROOF_TTL_MS) * 100));

  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2.5',
        expired ? 'border-danger bg-danger-subtle' : 'border-info bg-info-subtle',
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <p
          className={cn(
            'flex items-center gap-1.5 text-sm font-medium',
            expired ? 'text-danger-content' : 'text-info-content',
          )}
        >
          <IconClock size={15} aria-hidden="true" />
          {expired
            ? 'O prazo da confirmação de identidade expirou.'
            : 'Concluindo a assinatura. Não feche esta janela.'}
        </p>
        <span
          // O valor numérico não é anunciado a cada tick — ver comentário acima.
          aria-hidden="true"
          className={cn(
            'font-mono text-base font-semibold qc-tabular',
            expired ? 'text-danger-content' : 'text-info-content',
          )}
        >
          {formatCountdown(remaining)}
        </span>
      </div>

      <div
        role="progressbar"
        aria-valuenow={Math.round(percent)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Tempo restante da confirmação de identidade"
        className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface"
      >
        <div
          className={cn('h-full rounded-full transition-[width]', expired ? 'bg-danger' : 'bg-info')}
          style={{ width: `${percent}%` }}
        />
      </div>

      <span aria-live="polite" className="qc-sr-only">
        {announcement}
      </span>
    </div>
  );
}

export { classifyFailure as classifySignatureFailure };
